import { FOCUS_RING_INSET } from "./focus";

/**
 * Shared overlay classes for the v2 overlay family (redesign phase 5, #455):
 * Popover, DropdownMenu, Select, Combobox, and at implementation Venus's
 * ContextMenu, which reuses the MENU_* item styles on its own Radix parts.
 *
 * Surface (break #74): `--popover` fill, 1px `--border`, radius 8,
 * `shadow-popover`. Portalled, offset 6 like Tooltip, collision padding 8.
 *
 * Motion: a 150ms fade-in through `@starting-style`, as on Tooltip. No zoom or
 * slide, nothing under reduced motion. Closing is instant: Radix Presence only
 * waits for CSS animations, not transitions.
 *
 * Items (break #75): 32px rows (14/20 text + 6px padding, so a long label can
 * wrap), padding 8, radius 6 inside the content's 4px padding, 16px icons.
 * Highlight is `--hover-overlay` for pointer and keyboard alike (Radix focuses
 * the item under the pointer). The overlay alone is ~1.1:1 against the surface,
 * too faint as the only focus indicator, so a keyboard user also gets the
 * inset `--ring` outline: `:focus-visible` matches after Enter / arrow keys,
 * not after a mouse open. Stories show both statically through
 * `data-highlighted` / `data-focus`, as on Button; apps never set them.
 * The font family is set per component: Sora for menu commands, Inter for
 * Select / Combobox values.
 */

export const OVERLAY_OFFSET = 6;
export const OVERLAY_COLLISION_PADDING = 8;

export const OVERLAY_SURFACE =
  "z-50 rounded-lg border border-border bg-popover text-popover-foreground shadow-popover outline-hidden";

export const OVERLAY_FADE = "transition-opacity duration-150 starting:opacity-0 motion-reduce:transition-none";

/** Menu-like lists: surface, 4px padding, min width 160, scrolls inside the available height. */
export const MENU_CONTENT = `${OVERLAY_SURFACE} ${OVERLAY_FADE} min-w-40 overflow-y-auto p-1`;

/** Layout and text only. Combobox (cmdk) adds its own state classes: cmdk writes `data-disabled="false"`. */
export const MENU_ITEM_BASE =
  "relative flex min-h-8 cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-body outline-hidden";

/** Radix items (menus, Select): `data-highlighted` / `data-disabled` are present only while true. */
export const MENU_ITEM =
  `${MENU_ITEM_BASE} ` +
  "data-highlighted:bg-hover-overlay " +
  `${FOCUS_RING_INSET} ` +
  "data-disabled:pointer-events-none data-disabled:opacity-50";

/** Merged after MENU_ITEM (break #77). Icons take the text colour. */
export const MENU_ITEM_DESTRUCTIVE = "text-destructive-accent data-highlighted:bg-destructive-subtle";

/** Text starts where it would after a 16px icon or check: 8 + 16 + 8. */
export const MENU_INSET = "pl-8";

/** The leading 16px slot that holds an icon or the check (break #76). */
export const MENU_INDICATOR = "flex size-4 shrink-0 items-center justify-center";

/** The check of a selected / checked item: left, `--primary-accent`, normal text next to it. */
export const MENU_CHECK = `${MENU_INDICATOR} text-primary-accent`;

export const MENU_LABEL = "px-2 pt-2 pb-1 font-sans text-caption font-semibold text-ink-muted";

export const MENU_SEPARATOR = "-mx-1 my-1 h-px bg-divider";

export const MENU_SHORTCUT = "ml-auto pl-4 font-sans text-caption text-ink-muted";
